import React, { useState } from 'react';
import { BANKS } from '../constants';

const SUBSCRIPTION_CATEGORIES = [
  'Software/Tools',
  'Entertainment',
  'Cloud Storage',
  'Communication',
  'Marketing',
  'Design',
  'Other'
];

const getNextDueDate = (dueDay) => {
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  let nextDue = new Date(currentYear, currentMonth, dueDay);

  if (nextDue < today) {
    nextDue = new Date(currentYear, currentMonth + 1, dueDay);
  }
  return nextDue;
};

const isDueThisMonth = (sub) => {
  const today = new Date();
  const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  return sub.lastPaidMonth !== currentMonth;
};

const getStatus = (sub) => {
  const today = new Date();
  const dueDate = getNextDueDate(sub.dueDay);
  const daysDiff = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

  if (!isDueThisMonth(sub)) {
    return { label: 'Paid', color: 'green', icon: '✅' };
  } else if (daysDiff < 0) {
    return { label: 'Overdue', color: 'red', icon: '⚠️' };
  } else if (daysDiff <= 7) {
    return { label: 'Due Soon', color: 'orange', icon: '⏰' };
  } else {
    return { label: 'Upcoming', color: 'blue', icon: '📅' };
  }
};

const Subscriptions = ({ subscriptions, onAdd, onUpdate, onDelete, onMarkPaid, formatCurrency }) => {
  const [view, setView] = useState('list'); // list, add, edit
  const [selectedSub, setSelectedSub] = useState(null);
  const [filter, setFilter] = useState('all'); // all, paid, pending, overdue
  const [searchTerm, setSearchTerm] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    bankId: 'alfalah',
    alreadyPaid: false
  });
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    dueDay: '',
    category: 'Software/Tools',
    description: ''
  });

  const handleMarkPaid = (sub) => {
    setSelectedSub(sub);
    setPaymentData({ bankId: 'alfalah', alreadyPaid: false });
    setShowPaymentModal(true);
  };

  const handleConfirmPayment = async () => {
    await onMarkPaid(selectedSub, paymentData.bankId, paymentData.alreadyPaid);
    setShowPaymentModal(false);
    setSelectedSub(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedSub) {
      await onUpdate(selectedSub.id, formData);
    } else {
      await onAdd(formData);
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      amount: '',
      dueDay: '',
      category: 'Software/Tools',
      description: ''
    });
    setSelectedSub(null);
    setView('list');
  };

  const handleEdit = (sub) => {
    setSelectedSub(sub);
    setFormData({
      name: sub.name,
      amount: sub.amount,
      dueDay: sub.dueDay,
      category: sub.category || 'Software/Tools',
      description: sub.description || ''
    });
    setView('edit');
  };

  // Filter subscriptions
  const filteredSubs = subscriptions.filter(sub => {
    const matchesSearch = sub.name.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;

    if (filter === 'all') return true;
    const status = getStatus(sub);
    if (filter === 'paid') return status.label === 'Paid';
    if (filter === 'pending') return status.label === 'Upcoming' || status.label === 'Due Soon';
    if (filter === 'overdue') return status.label === 'Overdue';
    return true;
  });

  // Calculate stats
  const totalMonthly = subscriptions.reduce((sum, sub) => sum + (Number(sub.amount) || 0), 0);
  const activeCount = subscriptions.length;
  const paidCount = subscriptions.filter(sub => !isDueThisMonth(sub)).length;
  const overdueCount = subscriptions.filter(sub => getStatus(sub).label === 'Overdue').length;

  // List View
  if (view === 'list') {
    return (
      <div className="fade-in">
        <div className="flex justify-between items-center mb-6 flex-mobile-col">
          <h2 className="text-2xl font-bold text-yellow">💳 Subscriptions</h2>
          <button onClick={() => setView('add')} className="btn btn-primary">
            ➕ Add Subscription
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="stat-card" style={{ background: 'rgba(255,255,0,0.1)', border: '1px solid rgba(255,255,0,0.3)' }}>
            <p className="text-xs text-gray mb-1">💰 Monthly Cost</p>
            <p className="text-xl font-bold text-yellow">{formatCurrency(totalMonthly)}</p>
          </div>
          <div className="stat-card success">
            <p className="text-xs text-gray mb-1">📊 Active</p>
            <p className="text-xl font-bold text-green">{activeCount}</p>
          </div>
          <div className="stat-card" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)' }}>
            <p className="text-xs text-gray mb-1">✅ Paid</p>
            <p className="text-xl font-bold text-green">{paidCount}</p>
          </div>
          <div className="stat-card" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
            <p className="text-xs text-gray mb-1">⚠️ Overdue</p>
            <p className="text-xl font-bold text-red">{overdueCount}</p>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="mb-4">
          <div className="flex gap-2 mb-3 flex-wrap">
            <button
              onClick={() => setFilter('all')}
              className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
            >
              All
            </button>
            <button
              onClick={() => setFilter('paid')}
              className={`btn ${filter === 'paid' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
            >
              ✅ Paid
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`btn ${filter === 'pending' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
            >
              📅 Pending
            </button>
            <button
              onClick={() => setFilter('overdue')}
              className={`btn ${filter === 'overdue' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
            >
              ⚠️ Overdue
            </button>
          </div>
          <input
            type="text"
            className="form-input"
            placeholder="🔍 Search subscriptions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Subscriptions List */}
        {filteredSubs.length === 0 ? (
          <div className="card text-center py-8">
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💳</div>
            <p className="text-gray">
              {searchTerm ? 'No subscriptions found' : 'No subscriptions yet'}
            </p>
            <p className="text-muted text-sm">
              {searchTerm ? 'Try a different search term' : 'Add your first subscription to get started'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSubs.map(sub => {
              const status = getStatus(sub);
              const dueDate = getNextDueDate(sub.dueDay);

              return (
                <div key={sub.id} className="card">
                  <div className="flex justify-between items-start mb-3">
                    <div style={{ flex: 1 }}>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-xl font-bold text-yellow">{sub.name}</h3>
                        <span
                          className="text-xs px-2 py-1 rounded-full font-bold"
                          style={{
                            background: status.color === 'green' ? 'rgba(16,185,129,0.2)' :
                              status.color === 'red' ? 'rgba(239,68,68,0.2)' :
                                status.color === 'orange' ? 'rgba(245,158,11,0.2)' :
                                  'rgba(59,130,246,0.2)',
                            color: status.color === 'green' ? '#10b981' :
                              status.color === 'red' ? '#ef4444' :
                                status.color === 'orange' ? '#f59e0b' :
                                  '#3b82f6',
                            border: `1px solid ${status.color === 'green' ? '#10b981' :
                              status.color === 'red' ? '#ef4444' :
                                status.color === 'orange' ? '#f59e0b' :
                                  '#3b82f6'}`
                          }}
                        >
                          {status.icon} {status.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray">{sub.category}</p>
                      {sub.description && (
                        <p className="text-xs text-muted mt-1">{sub.description}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-green">{formatCurrency(sub.amount)}</p>
                      <p className="text-xs text-muted">per month</p>
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <p className="text-sm text-muted">
                      {status.label === 'Paid' ?
                        `Paid for ${new Date().toLocaleDateString('en-US', { month: 'long' })}` :
                        `Due: ${dueDate.toLocaleDateString()}`
                      }
                    </p>
                    <div className="flex gap-2">
                      {isDueThisMonth(sub) && (
                        <button
                          onClick={() => handleMarkPaid(sub)}
                          className="btn btn-primary"
                          style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                        >
                          💰 Mark Paid
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(sub)}
                        className="btn btn-secondary"
                        style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete ${sub.name}?`)) {
                            onDelete(sub.id);
                          }
                        }}
                        className="btn btn-danger"
                        style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                        title="Delete subscription"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentModal && selectedSub && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <div className="card" style={{ maxWidth: '500px', width: '100%' }}>
              <h3 className="text-xl font-bold mb-4">💰 Mark as Paid</h3>
              <p className="text-gray mb-4">
                Subscription: <span className="font-bold text-yellow">{selectedSub.name}</span>
              </p>
              <p className="text-lg font-bold text-green mb-4">
                Amount: {formatCurrency(selectedSub.amount)}
              </p>

              <div className="form-group">
                <label className="form-label">Pay From Bank *</label>
                <select
                  className="form-select"
                  value={paymentData.bankId}
                  onChange={(e) => setPaymentData({ ...paymentData, bankId: e.target.value })}
                  disabled={paymentData.alreadyPaid}
                >
                  {BANKS.map(bank => (
                    <option key={bank.id} value={bank.id}>{bank.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="flex items-center" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={paymentData.alreadyPaid}
                    onChange={(e) => setPaymentData({ ...paymentData, alreadyPaid: e.target.checked })}
                    style={{ marginRight: '0.5rem', width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span className="text-sm">Already paid (don't deduct from balance)</span>
                </label>
                <p className="text-xs text-muted mt-1">
                  Check this if you've already paid this subscription. This will only mark it as paid without creating a transaction or updating bank balance.
                </p>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleConfirmPayment}
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                >
                  ✅ Confirm Payment
                </button>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedSub(null);
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Add/Edit Form
  return (
    <div className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
      <button onClick={resetForm} className="btn btn-secondary mb-4">
        ← Back to List
      </button>
      <h2 className="text-2xl font-bold mb-6">
        {selectedSub ? '✏️ Edit Subscription' : '➕ Add Subscription'}
      </h2>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">Subscription Name *</label>
          <input
            type="text"
            className="form-input"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Netflix, GitHub Pro"
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Monthly Amount (PKR) *</label>
          <input
            type="number"
            className="form-input"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            placeholder="1000"
            min="0"
            step="1"
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Due Day of Month *</label>
          <input
            type="number"
            className="form-input"
            value={formData.dueDay}
            onChange={(e) => setFormData({ ...formData, dueDay: e.target.value })}
            placeholder="15"
            min="1"
            max="31"
            required
          />
          <p className="text-xs text-muted mt-1">Day of the month when payment is due (1-31)</p>
        </div>

        <div className="form-group">
          <label className="form-label">Category *</label>
          <select
            className="form-select"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            required
          >
            {SUBSCRIPTION_CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Description (Optional)</label>
          <textarea
            className="form-input"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Additional notes about this subscription..."
            rows="3"
          />
        </div>

        <div className="flex gap-3 mt-6">
          <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
            {selectedSub ? '✅ Update Subscription' : '✅ Add Subscription'}
          </button>
          <button type="button" onClick={resetForm} className="btn btn-secondary">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default Subscriptions;